import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Globe,
  Archive,
} from "lucide-react";
import {
  getPolicies,
  createPolicy,
  updatePolicy,
  deletePolicy,
} from "../services/adminPolicyService";

const PAGE_SIZE = 20;

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
};

const STATUS_STYLES = {
  active:       "bg-emerald-100 text-emerald-700",
  draft:        "bg-yellow-100 text-yellow-700",
  archived:     "bg-gray-100 text-gray-500",
  under_review: "bg-blue-100 text-blue-700",
};

const STATUS_OPTIONS    = ["draft", "active", "archived", "under_review"];

// ── Rich text editor (contentEditable, no extra dependency) ──────────────────
const TOOLBAR = [
  { cmd: "bold",          label: "B",  style: "font-bold" },
  { cmd: "italic",        label: "I",  style: "italic" },
  { cmd: "underline",     label: "U",  style: "underline" },
  { cmd: "strikeThrough", label: "S",  style: "line-through" },
  { cmd: "insertUnorderedList", label: "• List", style: "" },
  { cmd: "insertOrderedList",   label: "1. List", style: "" },
];
const HEADINGS = ["Normal", "Heading 1", "Heading 2", "Heading 3"];
const HEADING_TAGS = { "Normal": "p", "Heading 1": "h1", "Heading 2": "h2", "Heading 3": "h3" };

const ContentEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const skipSync  = useRef(false);

  // Seed initial value once
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, val) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val ?? null);
    skipSync.current = true;
    onChange(editorRef.current?.innerHTML ?? "");
  };

  const handleInput = () => {
    if (skipSync.current) { skipSync.current = false; return; }
    onChange(editorRef.current?.innerHTML ?? "");
  };

  return (
    <div className="rounded-lg border border-surface-border overflow-hidden focus-within:ring-2 focus-within:ring-[#5B4FCF]/25 focus-within:border-[#5B4FCF] transition">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-surface-border bg-gray-50">
        {/* Heading select */}
        <select
          onMouseDown={(e) => e.preventDefault()}
          onChange={(e) => exec("formatBlock", HEADING_TAGS[e.target.value])}
          defaultValue="Normal"
          className="text-xs border border-surface-border rounded px-1.5 py-1 bg-white text-brand-text focus:outline-none"
        >
          {HEADINGS.map((h) => <option key={h}>{h}</option>)}
        </select>
        <div className="w-px h-5 bg-surface-border mx-0.5" />
        {TOOLBAR.map(({ cmd, label, style }) => (
          <button
            key={cmd}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
            className={`px-2 py-1 text-xs rounded hover:bg-gray-200 text-brand-text transition ${style}`}
          >
            {label}
          </button>
        ))}
        <div className="w-px h-5 bg-surface-border mx-0.5" />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec("removeFormat"); }}
          className="px-2 py-1 text-xs rounded hover:bg-gray-200 text-brand-muted transition"
          title="Clear formatting"
        >
          Tx
        </button>
      </div>
      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        className="min-h-[220px] max-h-[380px] overflow-y-auto px-4 py-3 text-sm text-brand-text bg-white focus:outline-none prose prose-sm max-w-none"
      />
    </div>
  );
};

// ── Policy Form Modal (create + edit) ─────────────────────────────────────────
const PolicyFormModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial;
  const FIXED_POLICY_KEY = "privacy_policy";

  const [title,       setTitle]       = useState(initial?.title       ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [content,     setContent]     = useState(initial?.content     ?? "");
  const [status,      setStatus]      = useState(initial?.status      ?? "draft");
  const [versionNumber, setVersionNumber] = useState(initial?.version_number ?? "");
  const [makeCurrent, setMakeCurrent] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");

  const handleSave = async (publishNow = false) => {
    if (!title.trim())   { setError("Title is required."); return; }
    if (!content.trim()) { setError("Content is required."); return; }
    try {
      setSaving(true);
      setError("");
      const payload = {
        title:       title.trim(),
        policy_key: FIXED_POLICY_KEY,
        description: description.trim(),
        content:     content.trim(),
        status: publishNow ? "active" : status,
        ...(versionNumber !== "" ? { version_number: Number(versionNumber) } : {}),
        make_current: publishNow ? true : makeCurrent,
      };
      if (isEdit) {
        await updatePolicy(initial.id, payload);
      } else {
        await createPolicy(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message || "Failed to save policy.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border flex-shrink-0">
          <h3 className="text-base font-bold text-brand-text">
            {isEdit ? "Edit Policy" : "Create Policy"}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-brand-muted hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-brand-muted mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Data Privacy Policy"
              className="w-full px-3.5 py-2.5 rounded-lg border border-surface-border text-sm text-brand-text bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/25 focus:border-[#5B4FCF] transition"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-brand-muted mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description…"
              className="w-full px-3.5 py-2.5 rounded-lg border border-surface-border text-sm text-brand-text bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/25 focus:border-[#5B4FCF] transition"
            />
          </div>

          {/* Status
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-brand-muted mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-surface-border text-sm text-brand-text bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/25 focus:border-[#5B4FCF] transition"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</option>
                ))}
              </select>
            </div>
          </div>
          */}

          {/* Version number */}
          <div>
            <label className="block text-xs font-semibold text-brand-muted mb-1">Version Number (optional)</label>
            <input
              type="number"
              min="1"
              value={versionNumber}
              onChange={(e) => setVersionNumber(e.target.value)}
              placeholder="Leave empty for auto-increment"
              className="w-full px-3.5 py-2.5 rounded-lg border border-surface-border text-sm text-brand-text bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/25 focus:border-[#5B4FCF] transition"
            />
          </div>

          {/* Make current toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div
              onClick={() => setMakeCurrent((p) => !p)}
              className={`relative w-9 h-5 rounded-full transition-colors ${
                makeCurrent ? "bg-[#5B4FCF]" : "bg-gray-200"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  makeCurrent ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </div>
            <span className="text-sm text-brand-text font-medium">Make current (publish immediately)</span>
          </label>
          {makeCurrent && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              All other versions with the same policy key will become non-current.
            </p>
          )}

          {/* Content */}
          <div>
            <label className="block text-xs font-semibold text-brand-muted mb-1">
              Content <span className="text-red-500">*</span>
            </label>
            <ContentEditor value={content} onChange={setContent} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-border flex items-center justify-between flex-shrink-0">
          {error ? (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />{error}
            </p>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-brand-muted hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-surface-border text-brand-text hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? "Saving…" : "Save Draft"}
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#5B4FCF] text-white hover:bg-[#4a40b8] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? "Saving…" : "Publish Now"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Make Current confirm overlay ──────────────────────────────────────────────
const MakeCurrentConfirm = ({ policy, onClose, onConfirm, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
          <Globe className="w-4 h-4 text-emerald-600" />
        </div>
        <h3 className="text-base font-bold text-brand-text">Make Current?</h3>
      </div>
      <p className="text-sm text-brand-muted mb-1">
        <span className="font-semibold text-brand-text">{policy.title}</span> (v{policy.version_number})
      </p>
      <p className="text-sm text-brand-muted mb-6">
        This will become the publicly visible version. All other versions with the same key become non-current.
      </p>
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onClose}
          disabled={loading}
          className="px-4 py-2 rounded-lg text-sm text-brand-muted hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 transition-colors"
        >
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Publish
        </button>
      </div>
    </div>
  </div>
);

// ── Delete confirm overlay ────────────────────────────────────────────────────
const DeleteConfirm = ({ policy, onClose, onConfirm, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
          <Trash2 className="w-4 h-4 text-red-600" />
        </div>
        <h3 className="text-base font-bold text-brand-text">Delete Policy?</h3>
      </div>
      <p className="text-sm text-brand-muted mb-6">
        <span className="font-semibold text-brand-text">{policy.title}</span> (v{policy.version_number}) will be permanently deleted.
      </p>
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onClose}
          disabled={loading}
          className="px-4 py-2 rounded-lg text-sm text-brand-muted hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 transition-colors"
        >
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Delete
        </button>
      </div>
    </div>
  </div>
);

// ── Policy Row ─────────────────────────────────────────────────────────────────
const PolicyRow = ({ policy, serial, onEdit, onDelete, onMakeCurrent }) => {
  const [expanded, setExpanded] = useState(false);
  const status = (policy.status || "draft").toLowerCase();

  return (
    <>
      <tr className="border-b border-surface-border hover:bg-[#FAFAFA] transition-colors">
        <td className="px-4 py-3 text-sm text-brand-muted">{serial}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-brand-text">{policy.title}</span>
            {policy.is_current && (
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 leading-none">
                CURRENT
              </span>
            )}
          </div>
          <p className="text-xs text-brand-muted font-mono mt-0.5">{policy.policy_key}</p>
        </td>
        <td className="px-4 py-3 text-sm text-brand-muted">v{policy.version_number ?? "—"}</td>
        {/* <td className="px-4 py-3 text-xs text-brand-muted capitalize">{policy.category ?? "—"}</td> */}
        {/* <td className="px-4 py-3">
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLES[status] ?? STATUS_STYLES.draft}`}>
            {status.replace(/_/g, " ")}
          </span>
        </td> */}
        <td className="px-4 py-3 text-xs text-brand-muted whitespace-nowrap">{formatDate(policy.updatedAt ?? policy.updated_at ?? policy.createdAt ?? policy.created_at)}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            {/* {!policy.is_current && (
              <button
                onClick={() => onMakeCurrent(policy)}
                title="Make current"
                className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 transition-colors"
              >
                <Globe className="w-3.5 h-3.5" />
              </button>
            )} */}
            <button
              onClick={() => onEdit(policy)}
              title="Edit"
              className="p-1.5 rounded-md text-brand-muted hover:bg-[#ECE9FF] hover:text-[#5B4FCF] transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(policy)}
              title="Delete"
              className="p-1.5 rounded-md text-brand-muted hover:bg-red-50 hover:text-red-500 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setExpanded((p) => !p)}
              title={expanded ? "Hide content" : "Preview content"}
              className="p-1.5 rounded-md text-brand-muted hover:bg-[#ECE9FF] hover:text-[#5B4FCF] transition-colors"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-surface-border bg-[#F7F6FF]">
          <td colSpan={7} className="px-8 py-4">
            <p className="text-xs font-semibold text-brand-muted mb-2">Content preview</p>
            {policy.content ? (
              <div
                className="text-xs text-brand-muted leading-relaxed prose prose-xs max-w-none line-clamp-10"
                dangerouslySetInnerHTML={{ __html: policy.content }}
              />
            ) : (
              <p className="text-xs text-brand-light italic">No content loaded. Click Edit to view or update.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const AdminPoliciesPage = () => {
  const [policies,    setPolicies]    = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");

  // Filters
  const [filterStatus,   setFilterStatus]   = useState("");
  const [filterVersion, setFilterVersion] = useState("");
  const [filterCurrent,  setFilterCurrent]  = useState("");

  // Modals
  const [formModal,       setFormModal]       = useState(null);  // null | policy object | "new"
  const [deleteTarget,    setDeleteTarget]    = useState(null);
  const [currentTarget,   setCurrentTarget]   = useState(null);
  const [actionLoading,   setActionLoading]   = useState(false);
  const [successMsg,      setSuccessMsg]      = useState("");

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchPolicies = useCallback(async (p = page) => {
    setLoading(true);
    setError("");
    try {
      const params = { page: p, limit: PAGE_SIZE };
      params.policy_key = "privacy_policy";
      if (filterStatus)   params.status   = filterStatus;
      if (filterVersion !== "") params.version_number = filterVersion;
      if (filterCurrent)  params.is_current = filterCurrent;
      const res = await getPolicies(params);
      // Handle all known response shapes:
      // spec: { data: { policies: [...] }, pagination: {...} }
      // alt:  { policies: [...] }  |  { data: [...] }  |  [...]
      const data    = res?.data ?? res;
      const list    =
        Array.isArray(data?.policies)      ? data.policies :
        Array.isArray(res?.policies)       ? res.policies :
        Array.isArray(data?.data?.policies)? data.data.policies :
        Array.isArray(data)                ? data :
        [];
      const count =
        res?.pagination?.total ??
        data?.pagination?.total ??
        res?.total ??
        data?.total ??
        list.length;
      setPolicies(list);
      setTotal(count);
    } catch (err) {
      setError(err.message || "Failed to load policies.");
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterVersion, filterCurrent]);

  useEffect(() => {
    fetchPolicies(page);
  }, [page, filterStatus, filterVersion, filterCurrent, fetchPolicies]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await deletePolicy(deleteTarget.id);
      setDeleteTarget(null);
      showSuccess("Policy deleted.");
      fetchPolicies(page);
    } catch (err) {
      setError(err.message || "Delete failed.");
      setDeleteTarget(null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMakeCurrent = async () => {
    if (!currentTarget) return;
    setActionLoading(true);
    try {
      await updatePolicy(currentTarget.id, { make_current: true });
      setCurrentTarget(null);
      showSuccess(`"${currentTarget.title}" is now the current version.`);
      fetchPolicies(page);
    } catch (err) {
      setError(err.message || "Failed to publish.");
      setCurrentTarget(null);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#ECE9FF] flex items-center justify-center">
            <FileText className="w-5 h-5 text-[#5B4FCF]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-brand-text">Policies</h1>
            <p className="text-sm text-brand-muted">Manage policy versions shown on your public pages.</p>
          </div>
        </div>
        <button
          onClick={() => setFormModal("new")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-[#5B4FCF] text-white hover:bg-[#4a40b8] transition-colors"
        >
          <Plus className="w-4 h-4" /> New Policy
        </button>
      </div>

      {/* Success toast */}
      {successMsg && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-4">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          {successMsg}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-lg border border-surface-border text-sm text-brand-text bg-white focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/20 transition"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</option>
          ))}
        </select> */}
        <input
          type="number"
          min="1"
          value={filterVersion}
          onChange={(e) => { setFilterVersion(e.target.value); setPage(1); }}
          placeholder="Version #"
          className="px-3 py-2 rounded-lg border border-surface-border text-sm text-brand-text bg-white focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/20 transition"
        />
        <select
          value={filterCurrent}
          onChange={(e) => { setFilterCurrent(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-lg border border-surface-border text-sm text-brand-text bg-white focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/20 transition"
        >
          <option value="">All versions</option>
          <option value="true">Current only</option>
          <option value="false">Non-current only</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-surface-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-[#5B4FCF]" />
          </div>
        ) : policies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-brand-muted gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#F4F3FF] flex items-center justify-center">
              <Archive className="w-8 h-8 text-[#5B4FCF]/40" />
            </div>
            <p className="text-sm">No policies found. Create your first one.</p>
            <button
              onClick={() => setFormModal("new")}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-[#5B4FCF] text-white hover:bg-[#4a40b8] transition-colors"
            >
              <Plus className="w-4 h-4" /> New Policy
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-border bg-[#FAFAFA]">
                    {["#", "Title / Key", "Ver.", /* "Category", */ /* "Status", */ "Updated", ""].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold text-brand-muted uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {policies.map((p, i) => (
                    <PolicyRow
                      key={p.id}
                      policy={p}
                      serial={(page - 1) * PAGE_SIZE + i + 1}
                      onEdit={(pol) => setFormModal(pol)}
                      onDelete={(pol) => setDeleteTarget(pol)}
                      onMakeCurrent={(pol) => setCurrentTarget(pol)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-surface-border">
                <p className="text-xs text-brand-muted">
                  {total} polic{total !== 1 ? "ies" : "y"}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-surface-border text-brand-muted hover:bg-[#F4F3FF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-brand-muted">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-surface-border text-brand-muted hover:bg-[#F4F3FF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Modals ── */}
      {formModal && (
        <PolicyFormModal
          initial={formModal === "new" ? null : formModal}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            showSuccess(formModal === "new" ? "Policy created." : "Policy updated.");
            fetchPolicies(page);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirm
          policy={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          loading={actionLoading}
        />
      )}

      {currentTarget && (
        <MakeCurrentConfirm
          policy={currentTarget}
          onClose={() => setCurrentTarget(null)}
          onConfirm={handleMakeCurrent}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default AdminPoliciesPage;
